import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './MobileNavMenu.module.less';
import clsx from 'clsx';
import Tabs from './Tabs';
import ContentMenu from './ContentMenu';
import {
  tabInfoPropType,
  HAMBURGER_TOGGLE_ID,
  MOBILE_NAV_MENU_ID,
  MOBILE_NAV_CONTENT_MENU_BACK_BUTTON_ID,
  isElementVisible,
} from '../util';
import { SvgIcon } from 'edcom-ui';

export default function MobileNavMenu({ navInfo, isOpen, setIsOpen }) {
  const [contentMenu, setContentMenu] = useState(null);
  // State used to instantly hide the mobile nav if the user clicks a tab without a dropdown
  const [instantHideMenu, setInstantHideMenu] = useState(false);
  const menuContainerRef = useRef(null);

  useEffect(() => {
    // Handle clicking of Hamburger menu. Instant close if on ContentMenu
    const toggleMenu = () => {
      if (contentMenu) {
        setInstantHideMenu(true);
      } else {
        setIsOpen(open => !open);
      }
    };
    document.addEventListener('toggleMobileNavMenu', toggleMenu);
    return () =>
      document.removeEventListener('toggleMobileNavMenu', toggleMenu);
  }, [contentMenu]);

  useEffect(() => {
    if (instantHideMenu) {
      // After "instant closing" the menu, close the menu
      setIsOpen(false);
      // and revert the state back to false, so we can do it again if the menu is reopened
      setInstantHideMenu(false);
    }
  }, [instantHideMenu]);

  useEffect(() => {
    // Whenever we close the menu, unset the content menu (i.e., don't keep the Content Menu state on menu close)
    if (!isOpen) setContentMenu(null);
  }, [isOpen]);

  // Handle scrolling of background when this menu is open (not pretty)
  useEffect(() => {
    if (isOpen) {
      // Prevent scrolling on the background (body) when the menu (this component) is open
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
    } else {
      // Re-enable scrolling on the background when the menu is closed
      document.body.style.overflow = 'unset';
      document.body.style.position = '';

      // Unset the "scroll" of the inner component (Tabs or ContentMenu) so that, when you open menu -> scroll -> close
      // menu -> open menu again, we will not have a "saved" scrollTop property on the inner component in this last step
      if (
        menuContainerRef.current &&
        menuContainerRef.current.querySelectorAll('div')
      ) {
        menuContainerRef.current.querySelectorAll('div')[0].scrollTop = 0;
        document.getElementById(MOBILE_NAV_MENU_ID).scrollTop = 0;
      }
    }
    // Whenever the menu opens or changes (Tabs <--> ContentMenu), we scroll back to the top to line up the external
    // elements (EdCom logo + close icon) with the rest of the menu
    document.body.style.top = '0px';

    // ACCESSIBILITY:
    const hamburgerToggleButton = document.getElementById(HAMBURGER_TOGGLE_ID);
    const closeDropdownOnEsc = e => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setInstantHideMenu(true);
      hamburgerToggleButton.focus();
    };

    if (isOpen) {
      const allButtons = Array.from(
        menuContainerRef.current.querySelectorAll('button, a')
      ).filter(btn => isElementVisible(btn));

      if (!allButtons) return;
      // [Fabio] I couldn't find a good way to get the focus() to work without a timeout. The following SO has some info
      // https://stackoverflow.com/questions/55157914/setting-focus-via-a-ref-only-works-in-settimeout-in-react
      setTimeout(() => {
        // Focus on the first button or link in MobileNavMenu, excluding the "back button" in Content Menu
        const firstButton = menuContainerRef.current.querySelector(
          `button:not(#${MOBILE_NAV_CONTENT_MENU_BACK_BUTTON_ID})`
        );
        firstButton.focus();
      }, 50);

      // Hamburger menu
      hamburgerToggleButton.onkeydown = e => {
        if (e.key === 'Tab') {
          e.preventDefault();
          allButtons[!e.shiftKey ? 0 : allButtons.length - 1].focus();
        }
      };

      // First link
      allButtons[0].onkeydown = e => {
        if (e.key === 'Tab' && e.shiftKey) {
          e.preventDefault();
          hamburgerToggleButton.focus();
        }
      };

      // Last link
      allButtons[allButtons.length - 1].onkeydown = e => {
        if (e.key === 'Tab' && !e.shiftKey) {
          e.preventDefault();
          hamburgerToggleButton.focus();
        }
      };

      // TODO: Content menu has 2 "last links" the last link, and the last button. Currently, only the actual last link
      //       works. To fix this, we probably need some code in a useEffect in ContentMenu.

      // While the MobileNavMenu is open, close it if the Escape key is pressed
      window.addEventListener('keyup', closeDropdownOnEsc);
    } else {
      window.removeEventListener('keyup', closeDropdownOnEsc);
    }
  }, [isOpen, contentMenu]);

  const compensateForScroll = event => {
    const { scrollTop } = event.target;
    document.body.style.top = `-${scrollTop}px`;
  };

  const getTabInfo = () => navInfo.find(tab => tab.urlName === contentMenu);

  return (
    <>
      <nav
        id="mobile-nav-menu"
        className={clsx(
          styles.mobileNavMenu,
          isOpen && styles.expanded,
          contentMenu && styles.contentMenuOpen,
          instantHideMenu && styles.hidden
        )}
        role="navigation"
        aria-label="Main"
        onScroll={compensateForScroll}
      >
        <button
          id="mobile-nav-menu-close-button"
          className={clsx(styles.closeIcon, styles.desktopWidth)}
          type="button"
          aria-label="Close"
          aria-controls={MOBILE_NAV_MENU_ID}
          onClick={() => setIsOpen(false)}
        >
          <SvgIcon name="close" aria-hidden />
        </button>
        <div className={styles.menuContainer} ref={menuContainerRef}>
          {contentMenu ? (
            <ContentMenu
              tabInfo={getTabInfo()}
              backToMenu={() => setContentMenu(null)}
            />
          ) : (
            <Tabs
              navInfo={navInfo}
              onClick={setContentMenu}
              hideMenu={() => setInstantHideMenu(true)}
            />
          )}
        </div>
      </nav>
      <div
        className={clsx(
          styles.backgroundContainer,
          isOpen && styles.visible,
          instantHideMenu && styles.hidden
        )}
      />
    </>
  );
}

MobileNavMenu.propTypes = {
  navInfo: PropTypes.arrayOf(tabInfoPropType).isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
};
